import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AuthClientApi } from "../../../utils/api";
import NavbarMenu from "../../../Components/SharesNavbarMenu";
import { FaFilePdf } from "react-icons/fa";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import "./Shares.css";

function formatCurrency(amount) {
  if (!amount && amount !== 0) return "--";
  return new Intl.NumberFormat("en-US").format(amount);
}

export default function Shares() {
  const history = useHistory();
  const printRef = useRef(null);
  const tableRefs = useRef({});

  // Groups State
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupsPerPage] = useState(10);

  // State for each group's data
  const [groupStates, setGroupStates] = useState({});

  // Companies state
  const [companies, setCompanies] = useState([]);

  // Fetch Groups Metadata
  const fetchGroups = async (page = 1) => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-batch-groups-reports?limit=${groupsPerPage}&page=${page}`
      );
      const data = response.data;

      if (data.status && data.data && data.data.batches) {
        setGroups(data.data.batches);
        setTotalPages(data.data.pagination.totalPages);
        setTotalGroups(data.data.pagination.totalGroups);
        setCurrentPage(data.data.pagination.page);

        // Initialize group states
        const newGroupStates = {};
        data.data.batches.forEach((group) => {
          if (!groupStates[group.batch_id]) {
            newGroupStates[group.batch_id] = {
              isOpen: false,
              transactions: [],
              companies: [],
              loading: false,
            };
          }
        });

        if (Object.keys(newGroupStates).length > 0) {
          setGroupStates((prev) => ({ ...prev, ...newGroupStates }));
        }
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      setGroupsError(err.message);
      toast.error(err.message || "Failed to load groups");
    } finally {
      setGroupsLoading(false);
    }
  };

  // Fetch Transactions for a specific group
  const fetchGroupTransactions = async (batchId, page = 1) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        loading: true,
      },
    }));

    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-transaction-reports?limit=10&page=${page}&batchId=${batchId}`
      );
      const data = response.data;

      if (
        data.status &&
        data.data &&
        data.data.transactionReports &&
        Array.isArray(data.data.transactionReports)
      ) {
        // Extract unique companies
        const companiesMap = new Map();
        data.data.transactionReports.forEach((plan) => {
          if (!plan.planCompanies || !Array.isArray(plan.planCompanies)) return;
          plan.planCompanies.forEach((company) => {
            if (!companiesMap.has(company.companyName)) {
              companiesMap.set(company.companyName, {
                name: company.companyName,
                companyId: company.companyId,
                staticPrice: parseFloat(company.companyClosingPrice),
                customPrice: parseFloat(company.companyClosingPrice),
              });
            }
          });
        });
        const companiesFromAPI = Array.from(companiesMap.values());

        // Group transactions by planId
        const transactionsByPlan = new Map();

        data.data.transactionReports.forEach((plan) => {
          if (
            !plan.planCompanies ||
            !Array.isArray(plan.planCompanies) ||
            plan.planCompanies.length === 0
          )
            return;

          const planId = plan.planId;

          if (!transactionsByPlan.has(planId)) {
            const firstCompany = plan.planCompanies[0];
            transactionsByPlan.set(planId, {
              planId: planId,
              customerId: plan.customerId,
              accNumber: plan.trading_number,
              date: new Date(
                firstCompany.movedToTransactionAt
              ).toLocaleDateString("en-GB"),
              amounts: {},
              sharesMap: {},
              leftoverMap: {},
              companyStatus: {},
              transactions: {},
            });
          }

          const planData = transactionsByPlan.get(planId);

          plan.planCompanies.forEach((company) => {
            if (
              !company.transactionsReport ||
              !Array.isArray(company.transactionsReport) ||
              company.transactionsReport.length === 0
            )
              return;

            const transDetail = company.transactionsReport[0];

            planData.amounts[company.companyName] = parseFloat(
              company.allocatedAmount
            );
            planData.companyStatus[company.companyName] =
              company.transactionStatus === "Completed"
                ? "TransactionCompleted"
                : "InProgress";

            // Store details
            planData.sharesMap[company.companyName] =
              parseInt(transDetail.sharesPurchased) || 0;
            planData.leftoverMap[company.companyName] =
              parseFloat(transDetail.cashBalance) || 0;

            planData.transactions[company.companyName] = {
              transactionId: transDetail.transactionId,
              purchaseAmount: parseFloat(transDetail.purchaseAmount || 0),
              sharesPurchased: parseInt(transDetail.sharesPurchased) || 0,
              finalPrice: parseFloat(transDetail.finalPrice || 0),
              cashBalance: parseFloat(transDetail.cashBalance || 0),
              closingPrice: parseFloat(transDetail.closingPrice || 0),
              completedAt: transDetail.completedAt,
            };
          });
        });

        const transformedTransactions = Array.from(transactionsByPlan.values());

        // Update global companies if needed (or just use local for this batch)
        // We'll merge to keep a global list if needed, but for reports, batch-specific might be enough.
        // Let's update global to be safe.
        if (companies.length === 0) {
          setCompanies(companiesFromAPI);
        } else {
           const mergedCompanies = companiesFromAPI.map((apiCo) => {
            const existing = companies.find((c) => c.name === apiCo.name);
            return existing ? existing : apiCo;
          });
          setCompanies((prev) => {
              // Simple merge to avoid duplicates
              const existingNames = new Set(prev.map(c => c.name));
              const newCos = companiesFromAPI.filter(c => !existingNames.has(c.name));
              return [...prev, ...newCos];
          });
        }

        setGroupStates((prev) => ({
          ...prev,
          [batchId]: {
            ...prev[batchId],
            transactions: transformedTransactions,
            companies: companiesFromAPI,
            loading: false,
          },
        }));
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load transactions");
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          loading: false,
        },
      }));
    }
  };

  useEffect(() => {
    fetchGroups(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOpen = (batchId) => {
    const isCurrentlyOpen = groupStates[batchId]?.isOpen;

    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        isOpen: !isCurrentlyOpen,
      },
    }));

    if (!isCurrentlyOpen && groupStates[batchId]?.transactions.length === 0) {
      fetchGroupTransactions(batchId, 1);
    }
  };

  const handlePrintTablePDF = (batchId) => {
    const tableRef = tableRefs.current[batchId];
    if (!tableRef) return;

    try {
      const pdf = new jsPDF("landscape", "mm", "a4");

      const title = `Report - Group ${batchId}`;
      pdf.setFontSize(16);
      pdf.text(title, pdf.internal.pageSize.getWidth() / 2, 15, {
        align: "center",
      });
      pdf.setFontSize(10);

      const table = tableRef.querySelector("table");
      if (!table) {
        throw new Error("No table found");
      }

      const headerRow = table.querySelector("thead tr");
      const headers = Array.from(headerRow.querySelectorAll("th")).map((th) =>
        th.textContent.trim()
      );

      const bodyRows = Array.from(table.querySelectorAll("tbody tr"));

      const tableData = [];

      bodyRows.forEach((row) => {
        const rowData = Array.from(row.querySelectorAll("td")).map((td) => {
          if (td.classList.contains("company-value-cell")) {
            const lines = Array.from(td.querySelectorAll("div")).map((div) =>
              div.textContent.trim()
            );
            return lines.join("\n");
          }
          return td.textContent.trim();
        });
        tableData.push(rowData);
      });

      // Calculate totals programmatically for PDF only
      const groupState = groupStates[batchId];
      const currentTransactions = groupState.transactions;
      const currentCompanies = groupState.companies;

      // Build total row
      const totalRowData = ["Total", ""];
      currentCompanies.forEach((co) => {
        const totalAmount = currentTransactions.reduce(
          (sum, item) => sum + (item.amounts[co.name] || 0),
          0
        );
        const totalShares = currentTransactions.reduce(
          (sum, item) => sum + (item.sharesMap?.[co.name] || 0),
          0
        );
        const totalLeftover = currentTransactions.reduce(
          (sum, item) => sum + (item.leftoverMap?.[co.name] || 0),
          0
        );

        const totalText = `Purchase amount: ${formatCurrency(totalAmount)}\nShares: ${formatCurrency(totalShares)}\nTotal balance: ${formatCurrency(totalLeftover)}`;
        totalRowData.push(totalText);
      });

      // Add empty row and total row
      tableData.push(Array(headers.length).fill(""));
      tableData.push(totalRowData);

      const tableStyles = {
        theme: "grid",
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          halign: "left",
          fontSize: 8,
        },
        alternateRowStyles: {
          fillColor: [252, 252, 252],
        },
        columnStyles: {
          0: { fontStyle: "bold" },
          1: { halign: "center" },
        },
        margin: { top: 25 },
      };

      pdf.autoTable({
        head: [headers],
        body: tableData,
        ...tableStyles,
        didDrawPage: (data) => {
          pdf.setFontSize(8);
          pdf.text(
            `Page ${data.pageNumber} of ${pdf.getNumberOfPages()}`,
            pdf.internal.pageSize.getWidth() - 20,
            pdf.internal.pageSize.getHeight() - 10
          );
        },
        columnStyles: {
          ...tableStyles.columnStyles,
          2: { cellWidth: "auto" },
          3: { cellWidth: "auto" },
          4: { cellWidth: "auto" },
          5: { cellWidth: "auto" },
          6: { cellWidth: "auto" },
        },
        styles: {
          overflow: "linebreak",
          cellWidth: "wrap",
          fontSize: 8,
          cellPadding: 2,
        },
        willDrawCell: function (data) {
          const isLastRows = data.row.index >= tableData.length - 2;
          if (isLastRows && tableData.length > 2) {
            pdf.setFillColor(240, 240, 240);
            if (data.row.index === tableData.length - 1) {
              pdf.setFont(undefined, "bold");
            }
          }
        },
      });

      pdf.save(`table-report-${batchId}.pdf`);
    } catch (error) {
      console.error("Error creating PDF:", error);
      alert(`An error occurred while creating the PDF: ${error.message}`);
    }
  };

  const handleBackToDashboard = () => {
    history.push("/shares-platform");
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchGroups(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchGroups(currentPage + 1);
    }
  };

  return (
    <>
      <NavbarMenu />
      <div className="shares-wrapper">
        <button
          onClick={handleBackToDashboard}
          className="back-to-dashboard-btn"
          title="Back to Dashboard"
        >
          <ArrowBackIcon />
        </button>
        <h1 className="shares-title">Report</h1>

        <div className="shares-container fade-in" ref={printRef}>
          {groupsLoading && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {groupsError && (
            <div className="alert alert-danger" role="alert">
              Error loading reports: {groupsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => fetchGroups(currentPage)}
              >
                Retry
              </button>
            </div>
          )}

          {!groupsLoading && !groupsError && groups.length === 0 && (
            <div className="shares-empty">
              <p>No Completed Shares</p>
            </div>
          )}

          {!groupsLoading &&
            !groupsError &&
            groups.length > 0 &&
            groups.map((group, index) => {
              const groupState = groupStates[group.batch_id] || {
                isOpen: false,
                transactions: [],
                companies: [],
                loading: false,
              };

              const isOpen = groupState.isOpen;
              const currentTransactions = groupState.transactions;
              const currentCompanies = groupState.companies;
              const currentLoading = groupState.loading;
              const groupNumber = (currentPage - 1) * groupsPerPage + index + 1;

              return (
                <div className="shares-batch-card" key={group.batch_id}>
                  <div className="shares-batch-header">
                    <div className="shares-batch-info">
                      <span className="shares-batch-title">
                        Group No. {groupNumber}
                      </span>
                      {isOpen && (
                         <span
                           style={{
                             marginLeft: "15px",
                             color: "#666",
                             fontSize: "14px",
                           }}
                         >
                           Total Transactions: {currentTransactions.length}
                         </span>
                       )}
                    </div>
                    <div className="shares-batch-buttons">
                      <button
                        className="btn-shares-expand"
                        onClick={() => toggleOpen(group.batch_id)}
                      >
                        {isOpen ? "Collapse" : "Expand"}
                      </button>
                      {isOpen && (
                        <button
                          className="btn-print-table"
                          onClick={() => handlePrintTablePDF(group.batch_id)}
                          disabled={currentTransactions.length === 0}
                        >
                          <FaFilePdf className="icon-left" /> Print Report
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <>
                      {currentLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ padding: "40px" }}
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                        </div>
                      ) : currentTransactions.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#666",
                          }}
                        >
                          No transactions found for this group.
                        </div>
                      ) : (
                        <div className="custom-scroll">
                          <div
                            ref={(el) =>
                              (tableRefs.current[group.batch_id] = el)
                            }
                          >
                            <table className="table table-bordered shares-table">
                              <thead>
                                <tr className="shares-header-row">
                                  <th className="sticky-col">ACC #</th>
                                  <th className="sticky-col-2">DATE</th>
                                  {currentCompanies.map((co) => (
                                    <th
                                      key={co.name}
                                      className="company-header"
                                    >
                                      {co.name.toUpperCase()}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {currentTransactions.map((item, iItem) => (
                                  <tr key={iItem}>
                                    <td className="sticky-col">
                                      {item.accNumber}
                                    </td>
                                    <td className="sticky-col-2">
                                      {item.date}
                                    </td>

                                    {currentCompanies.map((co) => {
                                      const st =
                                        item.companyStatus?.[co.name];
                                      if (st === "TransactionCompleted") {
                                        const amount =
                                          item.amounts[co.name] || 0;
                                        const shares =
                                          item.sharesMap?.[co.name] || 0;
                                        const trans =
                                          item.transactions?.[co.name];
                                        const finalPrice =
                                          trans?.finalPrice || 0;
                                        const leftover =
                                          item.leftoverMap?.[co.name] || 0;

                                        if (amount === 0 && shares === 0) {
                                          return <td key={co.name}>--</td>;
                                        } else {
                                          return (
                                            <td
                                              key={co.name}
                                              className="company-value-cell"
                                            >
                                              <div className="amount-value">
                                                Purchase amount:{" "}
                                                {formatCurrency(amount)}
                                              </div>
                                              <div className="shares-value">
                                                Shares:{" "}
                                                {formatCurrency(shares)}
                                              </div>
                                              <div className="custom-value">
                                                Final Price:{" "}
                                                {formatCurrency(finalPrice)}
                                              </div>
                                              <div className="custom-value">
                                                Cash balance:{" "}
                                                {formatCurrency(leftover)}
                                              </div>
                                            </td>
                                          );
                                        }
                                      } else {
                                        return <td key={co.name}>--</td>;
                                      }
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="shares-total-row">
                                  <td className="sticky-col">Total</td>
                                  <td className="sticky-col-2"></td>
                                  {currentCompanies.map((co) => {
                                    const totalAmount =
                                      currentTransactions.reduce(
                                        (sum, item) =>
                                          sum + (item.amounts[co.name] || 0),
                                        0
                                      );
                                    const totalShares =
                                      currentTransactions.reduce(
                                        (sum, item) =>
                                          sum +
                                          (item.sharesMap?.[co.name] || 0),
                                        0
                                      );
                                    const totalLeftover =
                                      currentTransactions.reduce(
                                        (sum, item) =>
                                          sum +
                                          (item.leftoverMap?.[co.name] ||
                                            0),
                                        0
                                      );

                                    return (
                                      <td key={co.name}>
                                        <div>
                                          Purchase amount:{" "}
                                          {formatCurrency(totalAmount)}
                                        </div>
                                        <div>
                                          Shares:{" "}
                                          {formatCurrency(totalShares)}
                                        </div>
                                        <div>
                                          Total balance:{" "}
                                          {formatCurrency(totalLeftover)}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
        </div>

        {/* Pagination */}
        {!groupsLoading && !groupsError && groups.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 40px",
              marginTop: "20px",
            }}
          >
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              style={{
                padding: "10px 20px",
                backgroundColor: currentPage === 1 ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === 1 ? "hidden" : "visible",
              }}
            >
              ← Previous Page
            </button>

            <span style={{ color: "#666", fontSize: "14px" }}>
              Page {currentPage} of {totalPages} ({totalGroups} groups total)
            </span>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: "10px 20px",
                backgroundColor:
                  currentPage === totalPages ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === totalPages ? "hidden" : "visible",
              }}
            >
              Next Page →
            </button>
          </div>
        )}
      </div>
    </>
  );
}